import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
} from "react";
import {
	AboutDataStructure,
	Case,
	CaseDataProps,
	getAboutData,
	getCaseData,
	getHomeData,
	getProjectData,
	getTalents,
	getTalentsPageData,
	HomeData,
	ProjectDataProps,
	Talent,
	TalentDataProps,
	TalentPageDataProps,
} from "./utils/apiService";
import { useCallback } from "react";
import JotForm from "./components/JotForm";

type MainContextType = {
	//types
	homeData: HomeData | null;
	casesData: CaseDataProps | null;
	windowWidth: number;
	isMobile: boolean;
	getCaseById: (
		id: number,
		casesData?: CaseDataProps
	) => Case | null | undefined;
	getTalentById: (id: number) => Talent | null | undefined;
	getTalentBySlug: (slug: string, lang: string) => Talent | null | undefined;
	talentsData: TalentDataProps | null;
	aboutData: AboutDataStructure | null;
	projectData: ProjectDataProps | null;
	talentsPageData: TalentPageDataProps | null;
	jotFormOpen: boolean;
	setJotFormOpen: (open: boolean) => void;
	jotFormSrc: string;
	setJotFormSrc: (src: string) => void;
};

const MainContext = createContext<MainContextType>({
	//defaults
	homeData: null,
	projectData: null,
	windowWidth: 0,
	getCaseById: () => null,
	getTalentById: () => null,
	getTalentBySlug: () => null,
	isMobile: false,
	casesData: null,
	talentsData: null,
	talentsPageData: null,
	aboutData: null,
	jotFormOpen: false,
	setJotFormOpen: () => {},
	jotFormSrc: "",
	setJotFormSrc: () => {},
});

export const MainProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [windowWidth, setWindowWidth] = useState(0);
	const [isMobile, setIsMobile] = useState(false);
	const [homeData, setHomeData] = useState<HomeData | null>(null);
	const [talentsData, setTalentsData] = useState<TalentDataProps | null>(
		null
	);
	const [aboutData, setAboutData] = useState<AboutDataStructure | null>(null);
	const [casesData, setCasesData] = useState<CaseDataProps | null>(null);
	const [projectData, setProjectData] = useState<ProjectDataProps | null>(
		null
	);
	const [talentsPageData, setTalentsPageData] =
		useState<TalentPageDataProps | null>(null);
	const [jotFormOpen, setJotFormOpen] = useState(false);
	const [jotFormSrc, setJotFormSrc] = useState("");
	useEffect(() => {
		getHomeData().then(data => {
			setHomeData(data);
		});
		getTalents().then(data => {
			//@ts-expect-error data is not typed
			setTalentsData(data);
		});
		getAboutData().then(data => {
			setAboutData(data);
		});
		getProjectData().then(data => {
			setProjectData(data);
		});
		getCaseData().then(data => {
			setCasesData(data);
		});
		getTalentsPageData().then(data => {
			setTalentsPageData(data);
		});
	}, []);
	const getCaseById = (
		id: number,
		casesData?: CaseDataProps
	): Case | null | undefined => {
		if (!casesData) return null;
		if (!casesData.cases?.length) return null;
		return casesData.cases.find(item => item.id === id);
	};
	const getTalentById = useCallback(
		(id: number): Talent | null | undefined => {
			if (!talentsData?.talents?.length) return null;
			return talentsData?.talents.find(talent => talent.id === id);
		},
		[talentsData]
	);
	const getTalentBySlug = useCallback(
		(slug: string, lang: string): Talent | null | undefined => {
			if (!talentsData?.talents?.length) return null;
			return talentsData?.talents.find(
				talent =>
					(lang === "it" ? talent.url_it : talent.url_en) === slug
			);
		},
		[talentsData]
	);
	useEffect(() => {
		//starter to get window sizes without problems in SSR
		// Check if window is defined (this code runs only on the client side)
		if (typeof window !== "undefined") {
			const handleResize = () => {
				setWindowWidth(window.innerWidth);
				setIsMobile(window.innerWidth < 768);
			};

			// Initial width set
			handleResize();

			// Add event listener for resize
			window.addEventListener("resize", handleResize);

			// Cleanup event listener on unmount
			return () => {
				window.removeEventListener("resize", handleResize);
			};
		}
	}, []);

	//operazioni di fetch
	//axios etc..

	useEffect(() => {
		//on Start
	}, []);

	return (
		<MainContext.Provider
			value={{
				windowWidth,
				isMobile,
				homeData,
				aboutData,
				getCaseById,
				getTalentById,
				casesData,
				talentsData,
				projectData,
				getTalentBySlug,
				talentsPageData,
				jotFormOpen,
				setJotFormOpen,
				jotFormSrc,
				setJotFormSrc,
				/* your shared values */
			}}
		>
			{children}
			<JotForm
				open={jotFormOpen}
				setOpen={setJotFormOpen}
				src={jotFormSrc}
			/>
		</MainContext.Provider>
	);
};

export const useMainContext = () => useContext(MainContext);
